import { API_BASE } from '../config';
import React, { useState, useRef } from 'react';
import ReactDOM from 'react-dom';
import MediaCarousel from './MediaCarousel';
import UserProfile from './UserProfile';
import ProfilePicture from './ProfilePicture';
import { useToast } from '../context/ToastContext';
import ConfirmModal from './ui/ConfirmModal';
import useIsMobile from '../hooks/useIsMobile';

function ListingCard({ listing, onDelete, onEdit, onChat, onViewProfile, currentUser, showActions = true, isSaved = false, onToggleSave, initialFocused = false, onCloseFocus }) {
  const toast = useToast();
  const isMobile = useIsMobile();
  const [isFocused, setIsFocused] = useState(initialFocused);
  const [shareMenuOpen, setShareMenuOpen] = useState(false);
  const [isSaving, setIsSaving] = useState(false);
  const [sellerProfileModalOpen, setSellerProfileModalOpen] = useState(false);
  const [selectedSellerId, setSelectedSellerId] = useState(null);
  const [showDeleteConfirm, setShowDeleteConfirm] = useState(false);
  const [showCancelConfirm, setShowCancelConfirm] = useState(false);
  const isDeletingRef = useRef(false);
  const [isBuying, setIsBuying] = useState(false);
  const [isCancelling, setIsCancelling] = useState(false);
  const [showBuyingRequestModal, setShowBuyingRequestModal] = useState(false);
  const [modalError, setModalError] = useState('');

  const formatCategoryLabel = (rawCategory) => {
    if (!rawCategory) return 'Other';
    if (rawCategory === 'creative-goods') return 'Others';
    return rawCategory.replace(/-/g, ' ');
  };

  const handleDeleteClick = async (e) => {
    e.stopPropagation(); // Prevent card focus when clicking delete
    if (isDeletingRef.current) return; // Prevent multiple clicks
    // Show custom confirmation dialog
    setShowDeleteConfirm(true);
  };
  
  const confirmDelete = async () => {
    if (isDeletingRef.current) return;
    isDeletingRef.current = true;
    
    try {
      await onDelete(listing._id);
    } catch (error) {
      console.error('Failed to delete listing:', error);
      toast.error('Failed to delete listing. Please try again.');
    } finally {
      isDeletingRef.current = false;
      setShowDeleteConfirm(false);
    }
  };
  
  const cancelDelete = () => {
    setShowDeleteConfirm(false);
  };

  const handleEditClick = (e) => {     //handleEditClick is the function that is called when the edit button is clicked
    e.stopPropagation(); // Prevent card focus when clicking edit
    if (onEdit) {
      onEdit(listing);
    }
  };

  const handleBuyNow = async (e) => {
    e.stopPropagation();
    if (isBuying) return;
    // Phase 1: direct transactions only — always open the item request modal
    setShowBuyingRequestModal(true);
  };

  const handleDirectPaymentRequest = async (message = '') => {
    setIsBuying(true);
    try {
      const response = await fetch(`${API_BASE}/buying-requests`, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
        },
        credentials: 'include',
        body: JSON.stringify({ listingId: listing._id, message }),
      });

      if (!response.ok) {
        let errMsg = 'Failed to send item request.';
        try {
          const errJson = await response.json();
          if (errJson?.message) errMsg = errJson.message;
        } catch (e) {
          // ignore parse failure
        }
        throw new Error(errMsg);
      }

      toast.success('Item request sent!');
      setShowBuyingRequestModal(false);
    } catch (error) {
      console.error('Item Request Error:', error);
      if(error.message.toLowerCase().includes('pending request')){
        setModalError(error.message);
      }else{
        toast.error('Failed to send item request');
      }
    } finally {
      setIsBuying(false);
    }
  };

  const handleCancelSale = async (e) => {
    e.stopPropagation();
    if (isCancelling) return;

    setShowCancelConfirm(true);
  };

  const executeCancelSale = async () => {
    setIsCancelling(true);
    try {
      const response = await fetch(`${API_BASE}/listings/${listing._id}/cancel-transaction`, { method: 'POST', credentials: 'include' });

      if (!response.ok) {
        const data = await response.json().catch(()=>({}));
        throw new Error(data.message || 'Failed to cancel the transaction.');
      }

      toast.success('Transaction cancelled. The buyer has been notified.');
      listing.status = 'available';

    } catch (error) {
      console.error('Cancel Sale Error:', error);
      toast.error(error.message || 'An error occurred while cancelling the transaction. Please try again.');
    } finally {
      setIsCancelling(false);
    }
  };

  const handleCardClick = () => {     //handleCardClick is the function that is called when the card is clicked
    setIsFocused(true); //setIsFocused is the function that is called when the card is clicked
  };

  // Renders the share (+ optional save) control cluster. `dark` styles for use
  // over a photo/dark backdrop; otherwise neutral chips for light surfaces.
  const renderShareSave = ({ dark = false } = {}) => {
    const btnBase = {
      width: '36px',
      height: '36px',
      borderRadius: '50%',
      border: 'none',
      cursor: 'pointer',
      display: 'flex',
      alignItems: 'center',
      justifyContent: 'center',
      backgroundColor: dark ? 'rgba(0,0,0,0.45)' : 'rgba(255,255,255,0.95)',
      color: dark ? '#fff' : 'var(--maroon)',
      boxShadow: '0 1px 4px rgba(0,0,0,0.18)',
      transition: 'transform 0.15s ease, background-color 0.2s ease'
    };
    return (
      <div style={{ display: 'flex', gap: '0.4rem', position: 'relative' }}>
        {/* Save (bookmark) — hidden on your own listings */}
        {!isOwner && onToggleSave && (
          <button
            onClick={handleToggleSave}
            disabled={isSaving}
            aria-label={isSaved ? 'Remove from saved' : 'Save listing'}
            title={isSaved ? 'Saved' : 'Save listing'}
            style={btnBase}
            onMouseOver={(e) => e.currentTarget.style.transform = 'scale(1.08)'}
            onMouseOut={(e) => e.currentTarget.style.transform = 'scale(1)'}
          >
            <svg width="18" height="18" viewBox="0 0 24 24"
              fill={isSaved ? 'currentColor' : 'none'} stroke="currentColor" strokeWidth="2"
              strokeLinecap="round" strokeLinejoin="round">
              <path d="M19 21l-7-5-7 5V5a2 2 0 0 1 2-2h10a2 2 0 0 1 2 2z" />
            </svg>
          </button>
        )}

        {/* Share */}
        <button
          onClick={handleShare}
          aria-label="Share listing"
          title="Share listing"
          style={btnBase}
          onMouseOver={(e) => e.currentTarget.style.transform = 'scale(1.08)'}
          onMouseOut={(e) => e.currentTarget.style.transform = 'scale(1)'}
        >
          <svg width="18" height="18" viewBox="0 0 24 24"
            fill="none" stroke="currentColor" strokeWidth="2"
            strokeLinecap="round" strokeLinejoin="round">
            <circle cx="18" cy="5" r="3" />
            <circle cx="6" cy="12" r="3" />
            <circle cx="18" cy="19" r="3" />
            <line x1="8.59" y1="13.51" x2="15.42" y2="17.49" />
            <line x1="15.41" y1="6.51" x2="8.59" y2="10.49" />
          </svg>
        </button>

        {/* Desktop fallback menu (no Web Share API) */}
        {shareMenuOpen && (
          <div
            onClick={(e) => e.stopPropagation()}
            style={{
              position: 'absolute',
              top: '44px',
              right: 0,
              backgroundColor: '#fff',
              borderRadius: '10px',
              boxShadow: '0 8px 24px rgba(0,0,0,0.18)',
              border: '1px solid var(--border-subtle)',
              padding: '0.35rem',
              zIndex: 1100,
              minWidth: '160px'
            }}
          >
            {[
              { label: 'Copy link', fn: handleCopyLink },
              { label: 'Email', fn: handleShareEmail },
              { label: 'Text message', fn: handleShareSms },
            ].map(item => (
              <button
                key={item.label}
                onClick={item.fn}
                style={{
                  display: 'block',
                  width: '100%',
                  textAlign: 'left',
                  padding: '0.55rem 0.7rem',
                  background: 'transparent',
                  border: 'none',
                  borderRadius: '6px',
                  cursor: 'pointer',
                  fontSize: '0.9rem',
                  color: 'var(--text-strong)',
                  fontFamily: 'inherit'
                }}
                onMouseOver={(e) => e.currentTarget.style.backgroundColor = 'var(--surface-panel)'}
                onMouseOut={(e) => e.currentTarget.style.backgroundColor = 'transparent'}
              >
                {item.label}
              </button>
            ))}
          </div>
        )}
      </div>
    );
  };

  const handleCloseModal = () => {     //handleCloseModal is the function that is called when the modal is closed
    setIsFocused(false); //setIsFocused is the function that is called when the modal is closed
    if (onCloseFocus) onCloseFocus(); // let parent unmount deep-linked overlay cards
  };

  // ---- Share & Save helpers ----
  const getShareUrl = () =>
    `${window.location.origin}/dashboard?listing=${listing._id}`;

  const getShareText = () => {
    const price = listing.price > 0 ? ` ($${listing.price})` : '';
    return `Check out "${listing.title}"${price} on UTrade`;
  };

  const handleShare = async (e) => {
    if (e) e.stopPropagation();
    const url = getShareUrl();
    const shareData = { title: listing.title, text: getShareText(), url };
    // Native share sheet (mobile + supported desktop) → Contacts, Messages, Mail, etc.
    if (navigator.share) {
      try {
        await navigator.share(shareData);
      } catch (err) {
        // user cancelled — ignore (AbortError)
        if (err && err.name !== 'AbortError') console.error('Share failed:', err);
      }
      return;
    }
    // Fallback for browsers without the Web Share API
    setShareMenuOpen(prev => !prev);
  };

  const handleCopyLink = async (e) => {
    if (e) e.stopPropagation();
    try {
      await navigator.clipboard.writeText(getShareUrl());
      toast.success('Link copied!');
    } catch (err) {
      toast.error('Could not copy link.');
    }
    setShareMenuOpen(false);
  };

  const handleShareEmail = (e) => {
    if (e) e.stopPropagation();
    const subject = encodeURIComponent(getShareText());
    const body = encodeURIComponent(`${getShareText()}:\n${getShareUrl()}`);
    window.open(`mailto:?subject=${subject}&body=${body}`, '_blank');
    setShareMenuOpen(false);
  };

  const handleShareSms = (e) => {
    if (e) e.stopPropagation();
    const body = encodeURIComponent(`${getShareText()}: ${getShareUrl()}`);
    window.open(`sms:?&body=${body}`, '_blank');
    setShareMenuOpen(false);
  };

  const handleToggleSave = async (e) => {
    if (e) e.stopPropagation();
    if (isSaving || !onToggleSave) return;
    setIsSaving(true);
    try {
      await onToggleSave(listing, !isSaved);
    } finally {
      setIsSaving(false);
    }
  };

  // Helper to normalise ownerId for reliable comparison
  const ownerIdString = (() => {
    if (!listing) return null; //if the listing is not found, return null
    if (typeof listing.ownerId === 'object' && listing.ownerId !== null) {
      // owner populated: _id or id
      return (listing.ownerId._id ?? listing.ownerId.id)?.toString() ?? null;
    }
    if (typeof listing.ownerId === 'string') return listing.ownerId; //if the ownerId is a string, return the ownerId
    if (listing.ownerID) return listing.ownerID; //if the ownerID is found, return the ownerID
    return null; //if the ownerId is not found, return null
  })();

  const currentUserIdString = currentUser?._id?.toString() || currentUser?.id || null; //currentUserIdString is the current user's id

  const isOwner = !!currentUserIdString && !!ownerIdString && currentUserIdString === ownerIdString; //isOwner is the function that is called when the card is clicked

  // Get seller display name
  const getSellerName = () => {     //getSellerName is the function that is called when the card is clicked
    if (isOwner) {
      return 'You'; //if the user is the owner, return 'You'
    }
    
    // The server populates ownerId with user data
    if (listing.ownerId && typeof listing.ownerId === 'object') {
      return listing.ownerId.displayName || listing.ownerId.name || 'UMass Student'; //if the ownerId is found, return the ownerId's display name or name
    }
    
    // Fallback to other possible fields for seller info
    if (listing.sellerName) {
      return listing.sellerName; //if the sellerName is found, return the sellerName
    }
    if (listing.seller?.displayName) {
      return listing.seller.displayName; //if the seller's display name is found, return the seller's display name
    }
    if (listing.seller?.name) {
      return listing.seller.name; //if the seller's name is found, return the seller's name
    }
    if (listing.ownerName) {
      return listing.ownerName; //if the ownerName is found, return the ownerName
    }
    if (listing.owner?.displayName) {
      return listing.owner.displayName; //if the owner's display name is found, return the owner's display name
    }
    if (listing.owner?.name) {
      return listing.owner.name; //if the owner's name is found, return the owner's name
    }
    
    return 'UMass Student'; //if the sellerName, seller's display name, seller's name, ownerName, owner's display name, or owner's name is not found, return 'UMass Student'  
  };

  // Format seller name as "First L." for compact display
  const formatSellerName = (fullName) => {
    if (!fullName) return 'UMass Student';
    const parts = fullName.trim().split(' ');
    if (parts.length >= 2) {
      return `${parts[0]} ${parts[1][0]}.`;
    }
    return fullName;
  };

  // Get seller profile picture
  const getSellerProfilePicture = () => {
    // Handle populated owner object
    if (listing.ownerId && typeof listing.ownerId === 'object') {
      // Check specifically for profilePicture from Google
      if (listing.ownerId.profilePicture) {
        return listing.ownerId.profilePicture;
      }
    }
    
    // Handle cases where profile data might be in different fields
    if (listing.sellerProfilePicture) return listing.sellerProfilePicture;
    if (listing.seller?.profilePicture) return listing.seller.profilePicture;
    if (listing.owner?.profilePicture) return listing.owner.profilePicture;
    
    // If we got here, no profile picture was found
    return null;
  };

  // Prepare media for carousel
  const getMediaForCarousel = () => {
    const media = {
      images: [],
      video: null,
      thumbnailImage: listing.thumbnailImage || listing.imageURL
    };

    // Add images
    if (listing.images && listing.images.length > 0) {
      media.images = listing.images;
    } else if (listing.thumbnailImage || listing.imageURL) {
      media.images = [{ url: listing.thumbnailImage || listing.imageURL }];
    }

    // Add video if available
    if (listing.video) {
      media.video = listing.video;
    }

    return media;
  };

  console.log('ListingCard Debug:', {
    currentUser: currentUser?._id || currentUser?.id,
    listingOwner: listing?.ownerId || listing?.ownerID,
    isOwner,
    showActions,
    listing: listing?.title,
    sellerName: getSellerName()
  });

  // DeleteConfirmModal replaced by shared ConfirmModal component

  // Snackbar replaced by global ToastContext

  // Focused/Expanded Modal View
  if (isFocused) {
    const mediaData = getMediaForCarousel();
    
    return (
      <>
        {/* Backdrop */}
        <div
          style={{
            position: 'fixed',
            top: 0,
            left: 0,
            width: '100vw',
            height: '100vh',
            backgroundColor: 'rgba(0, 0, 0, 0.7)',
            zIndex: 1000,
            display: 'flex',
            alignItems: 'center',
            justifyContent: 'center',
            padding: isMobile ? '0' : '2rem'
          }}
          onClick={handleCloseModal}
        >
          {/* Modal Content */}
          <div
            style={{
              backgroundColor: '#fefefe',
              borderRadius: isMobile ? '0' : '16px',
              maxWidth: isMobile ? '100%' : '900px',
              width: '100%',
              height: isMobile ? '100%' : undefined,
              maxHeight: isMobile ? '100%' : '90vh',
              overflow: 'auto',
              position: 'relative',
              boxShadow: '0 20px 60px rgba(0,0,0,0.3)'
            }}
            onClick={(e) => e.stopPropagation()}
          >
            {/* Close Button */}
            <button
              onClick={handleCloseModal}
              style={{
                position: 'absolute',
                top: '1rem',
                right: '1rem',
                backgroundColor: 'rgba(0,0,0,0.1)',
                border: 'none',
                borderRadius: '50%',
                width: '40px',
                height: '40px',
                cursor: 'pointer',
                fontSize: '1.5rem',
                display: 'flex',
                alignItems: 'center',
                justifyContent: 'center',
                zIndex: 1001,
                transition: 'background-color 0.2s ease'
              }}
              onMouseOver={(e) => e.target.style.backgroundColor = 'rgba(0,0,0,0.2)'}
              onMouseOut={(e) => e.target.style.backgroundColor = 'rgba(0,0,0,0.1)'}
            >
              ×
            </button>

            {/* Share / Save cluster */}
            <div style={{
              position: 'absolute',
              top: '1rem',
              right: '4.25rem',
              zIndex: 1001
            }}>
              {renderShareSave({ dark: false })}
            </div>

            {/* Action Buttons - Show if user owns the listing */}
            {isOwner && showActions && (
              <div style={{
                position: 'absolute',
                top: '1rem',
                left: '1rem',
                display: 'flex',
                gap: '0.5rem',
                zIndex: 1001
              }}>
                {/* Edit Button */}
                {onEdit && !listing.pendingBuyingRequest && (
                  <button
                    onClick={handleEditClick}
                    style={{
                      padding: '0.5rem 0.75rem',
                      backgroundColor: 'var(--maroon)',
                      color: 'white',
                      border: 'none',
                      borderRadius: '6px',
                      cursor: 'pointer',
                      fontSize: '0.8rem',
                      fontWeight: '600',
                      display: 'flex',
                      alignItems: 'center',
                      gap: '0.25rem',
                      transition: 'all 0.2s ease',
                      boxShadow: '0 2px 4px rgba(136, 28, 28, 0.3)'
                    }}
                    onMouseOver={(e) => {
                      e.target.style.backgroundColor = 'var(--maroon-hover)';
                      e.target.style.transform = 'translateY(-1px)';
                    }}
                    onMouseOut={(e) => {
                      e.target.style.backgroundColor = 'var(--maroon)';
                      e.target.style.transform = 'translateY(0)';
                    }}
                  >
                    Edit
                  </button>
                )}

                {/* Delete Button */}
                <button
                  onClick={handleDeleteClick}
                  style={{
                    padding: '0.5rem 0.75rem',
                    backgroundColor: 'var(--maroon)',
                    color: 'white',
                    border: 'none',
                    borderRadius: '6px',
                    cursor: 'pointer',
                    fontSize: '0.8rem',
                    fontWeight: '600',
                    display: 'flex',
                    alignItems: 'center',
                    gap: '0.25rem',
                    transition: 'all 0.2s ease',
                    boxShadow: '0 2px 4px rgba(136, 28, 28, 0.3)'
                  }}
                  onMouseOver={(e) => {
                    e.target.style.backgroundColor = 'var(--maroon-hover)';
                  }}
                  onMouseOut={(e) => {
                    e.target.style.backgroundColor = 'var(--maroon)';
                  }}
                >
                  Delete
                </button>
              </div>
            )}

            <div style={{ padding: isMobile ? '1.25rem' : '2rem' }}>
              {/* Media Carousel */}
              {(mediaData.images.length > 0 || mediaData.video) && (
                <div style={{
                  margin: '1rem 0 2rem 0',
                  borderRadius: '12px',
                  overflow: 'hidden',
                  maxHeight: '480px'
                }}>
                  <MediaCarousel
                    images={mediaData.images}
                    video={mediaData.video}
                    thumbnailImage={mediaData.thumbnailImage}
                  />
                </div>
              )}

              {/* Title */}
              <h2 style={{
                margin: '0 0 1.5rem 0',
                fontSize: '2rem',
                fontWeight: '700',
                color: 'var(--maroon)',
                lineHeight: '1.2'
              }}>
                {listing.title}
              </h2>

              {/* Listing ID */}
              <div style={{fontSize:'1.2rem', fontWeight:'700', color:'#333', marginBottom:'0.75rem'}}>Item ID: {listing.itemId || listing._id.slice(-6).toUpperCase()}</div>

              {/* Seller Information */}
              <div style={{
                display: 'flex',
                alignItems: 'center',
                gap: '1rem',
                marginBottom: '1.5rem',
                padding: '1rem',
                backgroundColor: isOwner ? '#fff8e1' : '#f0efeb',
                borderRadius: '12px',
                border: `1px solid ${isOwner ? '#ffecb3' : '#e4e3e2'}`
              }}>
                {/* Profile Picture or Icon */}
                <ProfilePicture 
                  src={getSellerProfilePicture()}
                  alt={getSellerName()}
                  size="medium"
                  fallbackColor={isOwner ? '#ffc107' : 'var(--maroon)'}
                  fallbackContent=""
                />
                
                {/* Seller Details */}
                <div style={{ flex: 1 }}>
                  <div style={{
                    fontSize: '1.1rem',
                    fontWeight: '600',
                    color: isOwner ? 'var(--maroon)' : 'var(--maroon)',
                    marginBottom: '4px'
                  }}>
                    {isOwner ? 'Your Listing' : `Seller: ${formatSellerName(getSellerName())}`}
                  </div>
                  <div style={{
                    fontSize: '0.9rem',
                    color: 'var(--text-soft)',
                    display: 'flex',
                    alignItems: 'center',
                    gap: '0.25rem'
                  }}>
                    {/* removed decorative emoji */}
                  </div>
                </div>
                
                {/* Trust Badge */}
                {!isOwner && onViewProfile && (
                  <button
                    onClick={(e)=>{e.stopPropagation(); onViewProfile(listing);}}
                    style={{
                      padding:'0.45rem 1.5rem',
                      background:'var(--maroon)',
                      color:'#fff',
                      border:'none',
                      borderRadius:'6px',
                      cursor:'pointer',
                      fontSize:'0.9rem',
                      fontWeight:600,
                      transition:'all 0.2s ease'
                    }}
                    onMouseOver={(e)=>{
                      e.target.style.background='var(--maroon-hover)';
                    }}
                    onMouseOut={(e)=>{
                      e.target.style.background='var(--maroon)';
                    }}
                  >Seller Profile</button>
                )}
              </div>

              {/* Price Display */}
              <div style={{
                display: 'flex',
                alignItems: 'center',
                gap: '1rem',
                marginBottom: '1rem'
              }}>
                {/* Price */}
                <span style={{
                  fontSize: '1.6rem',
                  fontWeight: 'bold',
                  color: 'var(--maroon)'
                }}>
                  ${listing.price}
                </span>
                {listing.negotiable && (
                  <span style={{
                    fontSize: '1rem',
                    color: 'var(--maroon)'
                  }}>
                    (negotiable)
                  </span>
                )}
              </div>

              {/* Category and Negotiable */}
              <div style={{
                display: 'flex',
                justifyContent: 'space-between',
                alignItems: 'center',
                marginBottom: '2rem',
                flexWrap: 'wrap',
                gap: '1rem'
              }}>
                <span style={{
                  fontSize: '1rem',
                  color: 'var(--maroon)',
                  backgroundColor: 'rgba(136, 28, 28, 0.1)',
                  padding: '0.5rem 1rem',
                  borderRadius: '12px',
                  textTransform: 'capitalize',
                  fontWeight: '600'
                }}>
                  {formatCategoryLabel(listing.category)}
                </span>

                <div style={{ display: 'flex', gap: '1.5rem', alignItems: 'center' }}>
                  {/* Condition */}
                  <span style={{
                    fontSize: '0.95rem',
                    color: 'var(--text-soft)',
                    fontWeight: '600',
                    display: 'flex',
                    alignItems: 'center',
                    gap: '0.5rem'
                  }}>
                    Condition: <span style={{ textTransform: 'capitalize' }}>{listing.condition}</span>
                  </span>
                </div>
              </div>

              {/* Full Description */}
              <div style={{
                backgroundColor: '#f0efeb',
                padding: '1.5rem',
                borderRadius: '12px',
                border: '1px solid #e4e3e2'
              }}>
                <h3 style={{
                  margin: '0 0 1rem 0',
                  fontSize: '1.2rem',
                  color: 'var(--maroon)'
                }}>
                  Description
                </h3>
                <p style={{
                  margin: '0',
                  color: '#202020',
                  fontSize: '1rem',
                  lineHeight: '1.6',
                  whiteSpace: 'pre-wrap'
                }}>
                  {listing.description}
                </p>
              </div>
            </div>
          </div>
        </div>
      </>
    );
  }

  // Compact Card View (Default)
  return (
    <div style={{ height: '100%' }}>
      <style>{`
        @keyframes slideDown {
          from {
            transform: translateX(-50%) translateY(-20px);
            opacity: 0;
          }
          to {
            transform: translateX(-50%) translateY(0);
            opacity: 1;
          }
        }
      `}</style>
      <div
        style={{
          backgroundColor: 'var(--surface-card)',
          borderRadius: '16px',
          padding: '1.5rem',
          boxShadow: '0 1px 3px rgba(32,32,32,0.06)',
          position: 'relative',
          transition: 'transform 0.36s cubic-bezier(.165,.84,.44,1), box-shadow 0.36s cubic-bezier(.165,.84,.44,1), border-color 0.36s cubic-bezier(.165,.84,.44,1)',
          border: '1px solid var(--border-subtle)',
          cursor: 'pointer',
          height: '100%',
          display: 'flex',
          flexDirection: 'column'
        }}
        onClick={handleCardClick}
        onMouseOver={(e) => {
          e.currentTarget.style.boxShadow = '0 10px 30px rgba(32,32,32,0.10)';
          e.currentTarget.style.borderColor = '#d8d7d4';
          e.currentTarget.style.transform = 'translateY(-3px)';
        }}
        onMouseOut={(e) => {
          e.currentTarget.style.boxShadow = '0 1px 3px rgba(32,32,32,0.06)';
          e.currentTarget.style.borderColor = 'var(--border-subtle)';
          e.currentTarget.style.transform = 'translateY(0)';
        }}
      >
        {/* Listing Image */}
        {(listing.thumbnailImage || listing.imageURL) && (
          <div style={{
            width: '100%',
            height: '200px',
            borderRadius: '8px',
            marginBottom: '1rem',
            overflow: 'hidden',
            backgroundColor: '#f0efeb',
            position: 'relative'
          }}>
            <img
              src={listing.thumbnailImage || listing.imageURL}
              alt={listing.title}
              style={{
                width: '100%',
                height: '100%',
                objectFit: 'cover',
                filter: (listing.status === 'sold' || listing.status === 'completed' || listing.status === 'pending_confirmation' || listing.pendingBuyingRequest) ? 'grayscale(60%) opacity(0.6)' : 'none'
              }}
              onError={(e) => {
                e.target.style.display = 'none';
                e.target.parentElement.style.display = 'none';
              }}
            />
            {(listing.status === 'sold' || listing.status === 'completed' || listing.status === 'pending_confirmation' || listing.pendingBuyingRequest) && (
              <div style={{
                position: 'absolute',
                top: 0,
                left: 0,
                width: '100%',
                height: '100%',
                display: 'flex',
                alignItems: 'center',
                justifyContent: 'center',
                backgroundColor: 'rgba(0,0,0,0.4)',
                color: 'white',
                fontSize: '1.5rem',
                fontWeight: '700',
                textTransform: 'uppercase',
                letterSpacing: '2px'
              }}>
                {listing.status === 'pending_confirmation' ? 'Pending' : 
                 listing.pendingBuyingRequest ? 'BOOKED' : 'SOLD'}
              </div>
            )}
          </div>
        )}

        {/* Share / Save cluster — top-left over the image */}
        <div style={{
          position: 'absolute',
          top: '12px',
          left: '12px',
          zIndex: 5
        }} onClick={(e) => e.stopPropagation()}>
          {renderShareSave({ dark: true })}
        </div>

        {/* Action Buttons - Show if user owns the listing */}
        {isOwner && showActions && !isFocused && (
          <div style={{
            position: 'absolute',
            top: '12px',
            right: '12px',
            display: 'flex',
            gap: '0.5rem'
          }}>
            {/* Edit Button */}
            {onEdit && !listing.pendingBuyingRequest && (
              <button className="btn btn-primary btn-sm" onClick={handleEditClick}>
                Edit
              </button>
            )}

            {/* Delete Button */}
            <button className="btn btn-danger btn-sm" onClick={handleDeleteClick}>
              Delete
            </button>
          </div>
        )}

        {/* Listing Content */}
        <h3 style={{
          margin: '0 0 0.75rem 0',
          fontSize: '1.25rem',
          fontWeight: '600',
          color: 'var(--maroon)',
          lineHeight: '1.3',
          display: '-webkit-box',
          WebkitLineClamp: 2,
          WebkitBoxOrient: 'vertical',
          overflow: 'hidden',
          paddingRight: isOwner && showActions ? '120px' : '0' // Make room for buttons
        }}>
          {listing.title}
        </h3>

        {/* Action buttons for non-owners */}
        {!isOwner && (
          <div style={{ display: 'flex', gap: '0.5rem', marginBottom: '0.5rem' }}>
            {onChat && (
              <button
                className="btn btn-secondary btn-sm"
                style={{ flex: 1 }}
                onClick={(e)=>{e.stopPropagation(); onChat(listing);}}
              >Chat</button>
            )}
            {listing.price > 0 && listing.status !== 'sold' && listing.status !== 'completed' && !listing.pendingBuyingRequest && (
              <button
                className="btn btn-primary btn-sm"
                style={{ flex: 1 }}
                onClick={handleBuyNow}
                disabled={isBuying}
              >
                {isBuying ? 'Processing...' : 'Request Item'}
              </button>
            )}
          </div>
        )}

        {/* Seller Information */}
        <div style={{
          display: 'flex',
          alignItems: 'center',
          gap: '0.75rem',
          marginBottom: '0.75rem',
          padding: '0.75rem',
          backgroundColor: isOwner ? '#fff8e1' : '#f0efeb',
          borderRadius: '10px',
          border: `1px solid ${isOwner ? '#ffecb3' : '#e4e3e2'}`
        }}>
          {/* Profile Picture or Icon */}
          <ProfilePicture 
            src={getSellerProfilePicture()}
            alt={getSellerName()}
            size="small"
            fallbackColor={isOwner ? '#ffc107' : 'var(--maroon)'}
            fallbackContent=""
          />
          
          {/* Seller Details */}
          <div style={{ flex: 1, minWidth: 0 }}>
            <div style={{
              fontSize: '0.9rem',
              fontWeight: '600',
              color: 'var(--maroon)',
              whiteSpace: 'nowrap',
              overflow: 'hidden',
              textOverflow: 'ellipsis'
            }}>
              {isOwner ? 'Your Listing' : `Seller: ${formatSellerName(getSellerName())}`}
            </div>
          </div>
          
          {/* Trust Badge and Seller Profile */}
          {!isOwner && onViewProfile && (
            <button
              className="btn btn-secondary btn-sm"
              style={{ flexShrink:0, height:'30px', padding:'0 0.75rem', fontSize:'0.8rem' }}
              onClick={(e)=>{e.stopPropagation(); onViewProfile(listing);}}
            >Profile</button>
          )}
        </div>

        {/* Truncated Description - ONE LINE ONLY */}
        <p style={{
          margin: '0 0 1rem 0',
          color: 'var(--text-soft)',
          fontSize: '0.95rem',
          lineHeight: '1.5',
          whiteSpace: 'nowrap',
          overflow: 'hidden',
          textOverflow: 'ellipsis',
          height: '1.5rem' // Ensure exactly one line
        }}>
          {listing.description}
        </p>

        {/* Listing Details */}
        <div style={{
          display: 'flex',
          justifyContent: 'space-between',
          alignItems: 'center',
          marginBottom: '1rem',
          marginTop: 'auto',
          flexWrap: 'wrap',
          gap: '0.5rem'
        }}>
          {/* Price and Negotiable */}
          <div style={{
            display: 'flex',
            alignItems: 'center',
            gap: '1rem'
          }}>
            {/* Price */}
            <span style={{
              fontSize: '1.35rem',
              fontWeight: 'bold',
              color: 'var(--maroon)'
            }}>
              ${listing.price}
            </span>
            {listing.negotiable && (
              <span style={{
                fontSize: '0.85rem',
                color: 'var(--maroon)'
              }}>
                (negotiable)
              </span>
            )}
          </div>

            {/* Phase 1: all transactions are direct — no payment badge needed */}

        </div>

        {/* Category and Negotiable */}
        <div style={{
          display: 'flex',
          justifyContent: 'space-between',
          alignItems: 'center',
          marginBottom: '1rem'
        }}>
          <span style={{
            fontSize: '0.85rem',
            color: 'var(--maroon)',
            backgroundColor: 'rgba(136, 28, 28, 0.1)',
            padding: '0.25rem 0.5rem',
            borderRadius: '8px',
            textTransform: 'capitalize'
          }}>
            {formatCategoryLabel(listing.category)}
          </span>

          <div style={{ display: 'flex', gap: '0.5rem', alignItems: 'center' }}>
            
            
            {/* Condition Tag (only tag in bottom row) */}
            <span style={{
              fontSize: '0.85rem',
              color: 'var(--text-soft)',
              backgroundColor: '#f0efeb',
              padding: '0.25rem 0.5rem',
              borderRadius: '12px',
              textTransform: 'capitalize'
            }}>
              Condition: {listing.condition}
            </span>
          </div>
        </div>

        {/* Click to view more indicator */}
        <div style={{
          textAlign: 'center',
          padding: '0.5rem',
          backgroundColor: 'rgba(136, 28, 28, 0.05)',
          borderRadius: '8px',
          color: 'var(--maroon)',
          fontSize: '0.8rem',
          fontWeight: '500',
          marginTop: '0.5rem'
        }}>
          Click to view full details
        </div>
        
        {/* Delete confirmation modal */}
        <ConfirmModal
          open={showDeleteConfirm}
          title="Delete listing?"
          message={`"${listing.title}" will be removed. If there is a pending transaction, cancel it first.`}
          confirmLabel="Delete"
          danger
          onConfirm={() => { setShowDeleteConfirm(false); confirmDelete(); }}
          onCancel={cancelDelete}
        />


        {/* Item Request Modal */}
        {showBuyingRequestModal && (()=>{
          const modal = (
            <div
              style={{
                position: 'fixed',
                top: 0,
                left: 0,
                right: 0,
                bottom: 0,
                backgroundColor: 'rgba(0,0,0,0.7)',
                display: 'flex',
                justifyContent: 'center',
                alignItems: 'center',
                zIndex: 3000,
                animation: 'fadeIn 0.2s ease-out'
              }}
              onClick={() => setShowBuyingRequestModal(false)}
            >
              <style>{`@keyframes fadeIn{from{opacity:0}to{opacity:1}}@keyframes slideUp{from{transform:translateY(30px);opacity:0}to{transform:translateY(0);opacity:1}}`}</style>
              <div
                style={{
                  backgroundColor: 'white',
                  borderRadius: '16px',
                  padding: isMobile ? '1.5rem' : '2rem',
                  maxWidth: '500px',
                  width: '90%',
                  boxShadow: '0 20px 60px rgba(0,0,0,0.3)',
                  position: 'relative',
                  animation: 'slideUp 0.3s ease-out'
                }}
                onClick={(e) => e.stopPropagation()}
              >
                {/* Close (×) Button */}
                <button
                  onClick={() => setShowBuyingRequestModal(false)}
                  style={{
                    position: 'absolute',
                    top: '1rem',
                    right: '1rem',
                    background: 'rgba(0,0,0,0.1)',
                    border: 'none',
                    borderRadius: '50%',
                    width: '40px',
                    height: '40px',
                    cursor: 'pointer',
                    fontSize: '1.5rem',
                    lineHeight: '1',
                    display: 'flex',
                    alignItems: 'center',
                    justifyContent: 'center',
                    transition: 'background-color 0.2s ease'
                  }}
                  onMouseOver={(e)=>e.currentTarget.style.background='rgba(0,0,0,0.2)'}
                  onMouseOut={(e)=>e.currentTarget.style.background='rgba(0,0,0,0.1)'}
                >
                  ×
                </button>
                <h3 style={{ margin: '0 0 1rem 0', color: 'var(--maroon)', textAlign: 'center' }}>
                  Send Item Request
                </h3>
                {modalError && (
                  <div style={{ background:'#fff8e1', color:'var(--maroon)', border:'1px solid #f9c74f', borderRadius:8, padding:'0.6rem 0.75rem', marginBottom:'0.75rem', fontSize:'0.875rem', display:'flex', alignItems:'center', justifyContent:'space-between', gap:8 }}>
                    <span>{modalError}</span>
                    <button onClick={()=>setModalError('')} style={{ background:'none', border:'none', cursor:'pointer', color:'var(--maroon)', fontSize:'1.1rem', lineHeight:1, flexShrink:0 }}>×</button>
                  </div>
                )}
                {(() => {
                  const itemIdDisplay = listing.itemId || (listing._id ? listing._id.slice(-6).toUpperCase() : '');
                  return (
                    <p style={{ margin: '0 0 1.5rem 0', color: '#202020', textAlign: 'center', lineHeight: '1.5' }}>
                      Would you like to send an item request for
                      <br/>
                      <span style={{
                        fontWeight: '600',
                        color: 'var(--maroon)',
                        background: 'rgba(136, 28, 28, 0.08)',
                        padding: '0.25rem 0.75rem',
                        borderRadius: '8px',
                        display: 'inline-block',
                        marginTop: '0.4rem'
                      }}>
                        {listing.title}
                      </span>
                      <br/>
                      <span style={{ fontSize: '0.85rem', color: 'var(--text-soft)' }}>Item ID: {itemIdDisplay}</span>
                    </p>
                  );
                })()}

                <div style={{ marginBottom: '1.5rem' }}>
                  <div style={{
                    marginBottom: '0.75rem',
                    padding: '0.6rem 0.75rem',
                    borderRadius: '8px',
                    background: '#f0efeb',
                    border: '1px solid #e4e3e2',
                    color: '#555',
                    fontSize: '0.82rem',
                    lineHeight: '1.45'
                  }}>
                    The seller will approve or decline your request. If approved, coordinate campus pickup in chat.
                    Do not exchange money until you meet and inspect the item.
                  </div>
                  <label style={{ display: 'block', marginBottom: '0.5rem', fontWeight: '600', color: '#202020' }}>
                    Optional Message to Seller:
                  </label>
                  <textarea
                    id="buyingRequestMessage"
                    placeholder="Hi! I'm interested in buying this item. When would be a good time to meet?"
                    style={{
                      width: '100%',
                      height: '80px',
                      padding: '0.75rem',
                      border: '2px solid #e4e3e2',
                      borderRadius: '8px',
                      fontSize: '0.9rem',
                      resize: 'vertical',
                      fontFamily: 'inherit'
                    }}
                    maxLength={500}
                  />
                </div>

                <div style={{ display: 'flex', gap: '1rem' }}>
                  <button
                    onClick={(e) => {
                      e.stopPropagation();
                      const message = document.getElementById('buyingRequestMessage')?.value || '';
                      handleDirectPaymentRequest(message);
                    }}
                    disabled={isBuying}
                    style={{
                      flex: 1,
                      padding: '0.75rem',
                      backgroundColor: isBuying ? '#ccc' : 'var(--maroon)',
                      color: '#ffffff',
                      border: 'none',
                      borderRadius: '8px',
                      cursor: isBuying ? 'not-allowed' : 'pointer',
                      fontSize: '1rem',
                      fontWeight: '600'
                    }}
                  >
                    {isBuying ? 'Sending...' : 'Send Request'}
                  </button>

                  <button
                    onClick={(e) => {
                      e.stopPropagation();
                      setShowBuyingRequestModal(false);
                    }}
                    style={{
                      flex: 1,
                      padding: '0.75rem',
                      backgroundColor: 'transparent',
                      color: 'var(--text-soft)',
                      border: '2px solid #ddd',
                      borderRadius: '8px',
                      cursor: 'pointer',
                      fontSize: '1rem'
                    }}
                  >
                    Cancel
                  </button>
                </div>
              </div>
            </div>
          );
          return ReactDOM.createPortal(modal, document.body);
        })()}

        {/* ConfirmModal for cancel transaction */}
        <ConfirmModal
          open={showCancelConfirm}
          title="Cancel transaction?"
          message="The buyer will be notified. This action cannot be undone."
          confirmLabel="Cancel Transaction"
          danger
          onConfirm={() => { setShowCancelConfirm(false); executeCancelSale(); }}
          onCancel={() => setShowCancelConfirm(false)}
        />
      </div>
    </div>
  );
}

export default ListingCard; 